// ============================================================================
// Refund one order's unsourced/deallocated goods lines AND its still-refundable
// additional charges (freight/unloading/…) in one go.
//
// Mirrors `BatchConfirmModal` — one dialog, one order, tick-and-submit — on the
// money side. ONE call to the batch endpoint raises ONE escalation and cuts ONE
// Zoho invoice + ONE CN covering every picked goods line and charge.
//
// Charges eligibility is decided by Zoho itself: on open the modal calls
// `getRefundableCharges`, which reads the live Zoho SO's service-charge lines
// and returns those with `quantity_cancelled < quantity`. So a charge already
// refunded on a prior pass just doesn't appear — no OMS-side flag needed.
// ============================================================================
import { Alert, Button, Checkbox, Divider, Form, Input, InputNumber, Modal, Radio, Select, Space, Spin, Table, Tag, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ModalBody, { FLUSH_MODAL, MODAL_BODY_STYLE } from "../../components/modal_body";
import { fmtQty } from "../../format";
import { getRefundableCharges, getRefundableLines, REFUND_REASONS, RefundDestination, refundBatch } from "../../oms.api";
import { RefundableCharge, RefundableLine, SalesOrderDetail } from "../../types";
import { UserBankDetails, listUserBankDetails } from "../../returns/returns.api";
import { RefundDestinationPicker } from "../../_shared/RefundDestinationPicker";
import type {
  DestinationBlock,
  NewBankDetails,
} from "../../_shared/RefundDestinationPicker/types";

interface Props {
  open: boolean;
  salesOrderId: number | null;   // required to fetch charges when only charges are being refunded
  lines: RefundableLine[];
  // True when the SO's payment ledger carries any `authenticated` (3DS-completed but not yet
  // captured) Razorpay txn. When true, the modal double-checks with the operator before submitting
  // — the money is not yet in our bank, so refunding against it needs a finance sign-off.
  hasAuthenticatedPayment?: boolean;
  // Customer user id — threaded in so the Customer-destination bank-account dropdown can list +
  // add bank accounts. Required for customer-destination submits; omitted means the dropdown is
  // disabled and only wallet is usable.
  userId?: string | null;
  onClose: () => void;
  // Called with the latest sales-order snapshot returned by the refund — one refresh, not one
  // per line, and the drill-down does not have to refetch on its own.
  onRefunded: (so: SalesOrderDetail) => void;
}

interface Draft {
  picked: boolean;
  qty?: number;
}

const inr = new Intl.NumberFormat("en-IN", {
  style: "currency", currency: "INR", maximumFractionDigits: 2,
});

const chargeInclRate = (c: RefundableCharge): number =>
  c.rate_incl_tax != null ? Number(c.rate_incl_tax) : Math.round(Number(c.rate) * 118) / 100;

const chargeGrossRupees = (c: RefundableCharge, override?: string | number): number => {
  const rate = override !== undefined && override !== "" ? Number(override) : chargeInclRate(c);
  return Number.isFinite(rate) ? rate : 0;
};

const BatchRefundModal: React.FC<Props> = ({ open, salesOrderId, lines, hasAuthenticatedPayment, userId, onClose, onRefunded }) => {
  const [destination, setDestination] = useState<RefundDestination>("wallet");
  const [reason, setReason] = useState<string | undefined>();
  const [notes, setNotes] = useState("");
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [charges, setCharges] = useState<RefundableCharge[]>([]);
  const [chargesLoading, setChargesLoading] = useState(false);
  // Set when the refundable-charges fetch failed (Zoho outage / throttle / 5xx). Rendered as a
  // warning banner so operators distinguish "no refundable charges on this SO" from
  // "we couldn't reach Zoho to find out."
  const [chargesFetchError, setChargesFetchError] = useState<string | undefined>();
  const [pickedChargeIds, setPickedChargeIds] = useState<Set<string>>(new Set());
  // Operator-editable GST-inclusive rate per charge, keyed by Zoho `salesorder_item_id`. Empty /
  // absent = send Zoho's own rate. Edit = send `unit_price_incl_tax`.
  const [chargeRateById, setChargeRateById] = useState<Record<string, string>>({});
  // Bank-account capture for customer-destination refunds. Mirrors the universal-CN return/damage
  // modals so the Cash Log Refunds tab knows where to wire — captured upfront, not out-of-band.
  const [banks, setBanks] = useState<UserBankDetails[]>([]);
  const [bankDetailsId, setBankDetailsId] = useState<number | null>(null);
  // Inline-added account from the picker; sent as `destination_block.new_bank_details` and saved
  // server-side inside the refund-batch transaction.
  const [newBank, setNewBank] = useState<NewBankDetails | null>(null);
  // Inline bank-add is handled by RefundDestinationPicker's AddBankInline sub-form; no separate
  // AddBankAccountModal mount needed here.
  // Per-unit refund price by line id — starts as the Zoho-derived rate (or `unit_selling_price`
  // between open and fetch), then EDITABLE. Every edit becomes an `unit_price_rupees` override in
  // the batch payload; the backend re-prices the faux invoice + CN off that number and the
  // `_payment_covers` gate sees the resulting total (so refund > paid+tolerance is still refused).
  const [unitPriceById, setUnitPriceById] = useState<Record<number, string>>({});
  // Credit outstanding on the estimate, in rupees. Fetched alongside `getRefundableLines` on every
  // modal open. The ADR 2026-10-13 shrink/excess split is derived off this number: refunds that
  // fit entirely within credit outstanding have no excess, so the destination picker is HIDDEN —
  // the whole thing shrinks credit automatically. Refunds exceeding it keep the picker but ask it
  // only for the excess, not the full refund.
  const [creditOutstandingRupees, setCreditOutstandingRupees] = useState<number>(0);
  // Tracks lines whose rate the operator has typed into. A fresh Zoho fetch (see `useEffect`
  // below) is allowed to overwrite ONLY untouched lines — the operator's typed number wins over
  // anything that arrives afterwards on the same open.
  const editedLineIds = useRef<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);

  // The rate the modal actually prices against: Zoho-derived (tax-inclusive, discount-applied)
  // when available, else the estimate rate. Same helper for row totals and running total so they
  // agree byte-for-byte.
  const unitPriceOf = (l: RefundableLine): number =>
    Number(unitPriceById[l.id] ?? l.unit_selling_price);

  // `lines` is rebuilt by a `.filter().map()` in the parent, so it is a new array on any render
  // that re-runs it. Read it through a ref: the reset below must fire when the dialog OPENS, and
  // keying it on `lines` would also re-fire mid-edit on a parent re-render, wiping the operator's
  // picked rows and typed quantities and re-fetching the charges underneath them.
  const linesRef = useRef(lines);
  linesRef.current = lines;

  // Reset every open — an operator returning to the dialog after a partial refund should see the
  // fresh remaining refundable qty on each line and the fresh Zoho-derived charge list.
  useEffect(() => {
    if (!open) return;
    setDestination("wallet");
    setReason(undefined);
    setNotes("");
    setDrafts(Object.fromEntries(
      linesRef.current.map((l) => [l.id, { picked: false, qty: Number(l.refundable_qty) }])));
    setPickedChargeIds(new Set());
    setCharges([]);
    setChargeRateById({});
    setUnitPriceById({});
    setBankDetailsId(null);
    setBanks([]);
    setCreditOutstandingRupees(0);
    editedLineIds.current = new Set();
    if (salesOrderId != null) {
      setChargesLoading(true);
      getRefundableCharges(salesOrderId).then(({ rows, fetchError }) => {
        setCharges(rows);
        setChargesFetchError(fetchError);
        setChargesLoading(false);
      });
      // Fetched alongside charges — the modal opens with estimate rates in place and swaps them
      // for the Zoho-priced values as soon as this resolves. Any line the operator has already
      // typed into keeps its edit — the Zoho number wins only on lines still untouched.
      // The same response carries `credit_outstanding_paise` for the ADR 2026-10-13 split preview.
      getRefundableLines(salesOrderId).then(({ rows, credit_outstanding_paise }) => {
        setUnitPriceById((prev) => {
          const next = { ...prev };
          for (const r of rows) {
            if (!editedLineIds.current.has(r.line_id)) {
              next[r.line_id] = r.unit_refund_price;
            }
          }
          return next;
        });
        setCreditOutstandingRupees(Number(credit_outstanding_paise) / 100);
      });
    }
  }, [open, salesOrderId]);

  // Load the customer's bank accounts the first time destination=customer is picked (per modal
  // open). Reuses the same `/oms/users/<id>/bank-details/` endpoint the universal-CN flows use.
  useEffect(() => {
    if (destination === "customer" && userId && banks.length === 0) {
      listUserBankDetails(userId).then(setBanks);
    }
  }, [destination, userId, banks.length]);

  const pickedLines = useMemo(() => lines.filter((l) => drafts[l.id]?.picked), [lines, drafts]);
  const pickedCharges = useMemo(
    () => charges.filter((c) => pickedChargeIds.has(c.salesorder_item_id)),
    [charges, pickedChargeIds]
  );

  const totalRupees =
    pickedLines.reduce((sum, l) => sum + (drafts[l.id]?.qty ?? 0) * unitPriceOf(l), 0) +
    pickedCharges.reduce(
      (sum, c) => sum + chargeGrossRupees(c, chargeRateById[c.salesorder_item_id]),
      0,
    );

  // ADR 2026-10-13 shrink/excess split, computed live as the operator picks rows / edits qty.
  // `creditShrinkRupees` is paid down against outstanding credit via a wallet_credit_settlement
  // EPT; `excessRupees` is what remains for the operator-chosen destination (wallet/customer).
  // When excess is 0 (refund ≤ credit outstanding), the destination picker is HIDDEN — there is
  // nothing to route.
  const creditShrinkRupees = Math.min(totalRupees, creditOutstandingRupees);
  const excessRupees = Math.max(totalRupees - creditShrinkRupees, 0);
  const hasExcess = excessRupees > 0;
  const hasCreditShrink = creditShrinkRupees > 0;

  // If there's no excess the picker is hidden and we hardcode destination=wallet on submit — the
  // backend treats it the same (both run through the shrink path). The customer-side bank-detail
  // requirement only matters when there IS an excess the operator chose to route to customer.
  const effectiveDestination: RefundDestination = hasExcess ? destination : "wallet";

  // A picked LINE with a qty outside its refundable range is invalid; unpicked lines contribute
  // nothing so their qty is ignored. Charges refund in FULL so no per-row qty check applies.
  const invalidLine = pickedLines.find((l) => {
    const d = drafts[l.id];
    const max = Number(l.refundable_qty);
    return d?.qty == null || d.qty <= 0 || d.qty > max;
  });

  const nothingPicked = !pickedLines.length && !pickedCharges.length;

  const submit = async () => {
    if (salesOrderId == null) return;
    // Backend refuses customer-destination submits without a bank account — surface that upfront
    // so the operator doesn't lose the modal state to a 400 toast. Only applies when there's an
    // excess being routed to customer; the no-excess case is forced to wallet, no bank needed.
    if (effectiveDestination === "customer" && !bankDetailsId && !newBank) {
      message.error("Pick a bank account for the customer refund.");
      return;
    }
    setSaving(true);
    // ONE call: the batch endpoint raises ONE escalation and cuts ONE Zoho invoice + ONE CN for
    // the whole batch (goods lines + additional charges together). Failure is all-or-nothing.
    const nextSo = await refundBatch(salesOrderId, {
      items: pickedLines.map((l) => ({
        line_id: l.id,
        qty: drafts[l.id]!.qty!,
        // Send an explicit override only for lines the operator actually typed into. An
        // untouched line stays priced by the backend off the live Zoho SO, exactly as before.
        ...(editedLineIds.current.has(l.id)
          ? { unit_price_rupees: unitPriceById[l.id] }
          : {}),
      })),
      additional_charges: pickedCharges.map((c) => {
        const override = chargeRateById[c.salesorder_item_id];
        return {
          salesorder_item_id: c.salesorder_item_id,
          // Send the override only when the operator actually typed into the Rate cell. An
          // untouched row stays priced off Zoho's own `rate` on the SO service-charge line.
          ...(override !== undefined && override !== "" ? { unit_price_incl_tax: override } : {}),
        };
      }),
      destination: effectiveDestination,
      reason_code: reason,
      notes: notes || undefined,
      bank_details_id: effectiveDestination === "customer" ? bankDetailsId : null,
      ...(effectiveDestination === "customer" && newBank
        ? { destination_block: { destination: "customer", new_bank_details: newBank } }
        : {}),
    });
    setSaving(false);
    if (nextSo) {
      onRefunded(nextSo);
      onClose();
    }
  };

  const handleOk = () => {
    if (nothingPicked || salesOrderId == null) return;
    // Only 3DS-authenticated (not yet captured) money covers this refund on the ledger — the bank
    // hasn't debited the customer yet. Backend accepts it, but this is the "have you spoken to
    // finance?" moment. Operator confirms → submit; cancels → dialog stays open.
    if (hasAuthenticatedPayment) {
      Modal.confirm({
        title: "Payment not yet authorised",
        content:
          "Are you sure to move forward without an authorised payment? Confirm with finance to " +
          "authorise the given estimate's payment.",
        okText: "Yes, proceed",
        cancelText: "No",
        onOk: submit,
      });
      return;
    }
    submit();
  };

  const lineColumns: ColumnsType<RefundableLine> = [
    {
      title: "",
      key: "pick",
      width: 40,
      render: (_, r) => {
        const refundable = Number(r.refundable_qty);
        return (
          <Checkbox
            checked={!!drafts[r.id]?.picked}
            // Fully-allocated / already-refunded lines carry nothing to act on. We still render
            // the row (so the operator sees the full line context) but disable the pick — the
            // charge-only refund below is the exit here.
            disabled={!(refundable > 0)}
            onChange={(e) =>
              setDrafts((d) => ({ ...d, [r.id]: { ...d[r.id], picked: e.target.checked } }))
            }
          />
        );
      },
    },
    {
      title: "Product",
      key: "product",
      render: (_, r) => (
        <div>
          <div style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            <span>{r.product?.product_name || "—"}</span>
            {r.product?.is_sample && (
              <Tag color="gold" style={{ margin: 0, fontSize: 10, lineHeight: "16px", padding: "0 4px" }}>
                Sample
              </Tag>
            )}
            {r.product?.is_service && (
              <Tag color="blue" style={{ margin: 0, fontSize: 10, lineHeight: "16px", padding: "0 4px" }}>
                Service
              </Tag>
            )}
          </div>
          {r.product?.private_label_name && (
            <div style={{ fontSize: 12, color: "#531dab" }}>
              Private label: {r.product.private_label_name}
            </div>
          )}
          {r.product?.sku && (
            <div style={{ fontSize: 11, color: "#999" }}>SKU {r.product.sku}</div>
          )}
        </div>
      ),
    },
    {
      title: "Refund qty",
      key: "qty",
      width: 130,
      render: (_, r) => {
        const d = drafts[r.id];
        const max = Number(r.refundable_qty);
        return (
          <div>
            <InputNumber
              size="small"
              min={0}
              max={max}
              precision={3}
              disabled={!d?.picked}
              value={d?.qty}
              style={{ width: "100%" }}
              onChange={(v) =>
                setDrafts((prev) => ({
                  ...prev,
                  [r.id]: { ...prev[r.id], qty: v == null ? undefined : Number(v) },
                }))
              }
            />
            <div style={{ fontSize: 11, color: "#999" }}>
              max {fmtQty(r.refundable_qty)}
              {r.unit ? ` ${r.unit}` : ""}
            </div>
          </div>
        );
      },
    },
    {
      title: "Rate (incl. GST)",
      key: "rate",
      width: 120,
      render: (_, r) => {
        const d = drafts[r.id];
        return (
          <InputNumber
            size="small"
            min={0}
            precision={2}
            disabled={!d?.picked}
            value={unitPriceOf(r)}
            style={{ width: "100%" }}
            onChange={(v) => {
              // A blank field means "reset to the fetched Zoho rate" — clear from both the map and
              // the edited-set so the next fetch (if any) can overwrite it again. Any typed number
              // is remembered as an edit even if it happens to equal the Zoho value; the operator
              // touched it, and the backend gets the explicit override.
              if (v == null) {
                setUnitPriceById((prev) => {
                  const next = { ...prev };
                  delete next[r.id];
                  return next;
                });
                editedLineIds.current.delete(r.id);
                return;
              }
              editedLineIds.current.add(r.id);
              setUnitPriceById((prev) => ({ ...prev, [r.id]: String(v) }));
            }}
          />
        );
      },
    },
    {
      title: "Total",
      key: "total",
      width: 120,
      render: (_, r) => {
        const d = drafts[r.id];
        const qty = d?.picked ? (d.qty ?? 0) : 0;
        return inr.format(qty * unitPriceOf(r));
      },
    },
  ];

  const chargeColumns: ColumnsType<RefundableCharge> = [
    {
      title: "",
      key: "pick",
      width: 40,
      render: (_, r) => (
        <Checkbox
          checked={pickedChargeIds.has(r.salesorder_item_id)}
          onChange={(e) =>
            setPickedChargeIds((s) => {
              const next = new Set(s);
              if (e.target.checked) next.add(r.salesorder_item_id);
              else next.delete(r.salesorder_item_id);
              return next;
            })
          }
        />
      ),
    },
    {
      title: "Charge",
      key: "name",
      render: (_, r) => (
        <div style={{ fontSize: 13 }}>{r.name || "—"}</div>
      ),
    },
    {
      title: "Rate (incl. GST)",
      key: "rate",
      width: 150,
      render: (_, r) => {
        const picked = pickedChargeIds.has(r.salesorder_item_id);
        const override = chargeRateById[r.salesorder_item_id];
        const value = override !== undefined && override !== "" ? Number(override) : chargeInclRate(r);
        return (
          <InputNumber
            size="small"
            min={0}
            precision={2}
            disabled={!picked}
            value={value}
            style={{ width: "100%" }}
            onChange={(v) => {
              setChargeRateById((prev) => {
                const next = { ...prev };
                if (v == null) {
                  delete next[r.salesorder_item_id];
                } else {
                  next[r.salesorder_item_id] = String(v);
                }
                return next;
              });
            }}
          />
        );
      },
    },
    {
      title: "Total (incl. GST)",
      key: "total",
      width: 140,
      render: (_, r) =>
        inr.format(chargeGrossRupees(r, chargeRateById[r.salesorder_item_id])),
    },
  ];

  const orderId = salesOrderId ?? "";

  return (
    <>
    <Modal
      title={`Refund — SO #${orderId}`}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText="Mark refund"
      okButtonProps={{
        danger: true,
        disabled: nothingPicked || !!invalidLine || !reason
                  || (effectiveDestination === "customer" && !bankDetailsId && !newBank),
      }}
      confirmLoading={saving}
      destroyOnClose
      centered
      width={860}
      className={FLUSH_MODAL}
      bodyStyle={MODAL_BODY_STYLE}
    >
      <ModalBody>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {/* ADR 2026-10-13 split preview — surfaces the credit-shrink portion the operator
              CANNOT re-route, and the excess portion they CAN. When there's no excess, the
              picker is hidden entirely: there's nothing to decide, everything shrinks credit. */}
          {hasCreditShrink && (
            <Alert
              type="info"
              showIcon
              message={
                hasExcess
                  ? `${inr.format(creditShrinkRupees)} will shrink the customer's outstanding credit.`
                  : `${inr.format(creditShrinkRupees)} will shrink the customer's outstanding credit. Nothing else needs routing.`
              }
            />
          )}

          {hasExcess && salesOrderId != null && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                Where does the {inr.format(excessRupees)} excess go?
              </Typography.Text>
              <RefundDestinationPicker
                // refund-batch is the money commitment — dont_know is refused by the server, so
                // the Authorize mount (which hides dont_know) is the right shape here.
                mount="authorize"
                salesOrderId={salesOrderId}
                refundAmountPaise={Math.round(excessRupees * 100)}
                userId={userId ?? null}
                savedBankDetails={banks}
                initial={destinationBlockFromState(destination, bankDetailsId)}
                onChange={(block) => {
                  if (block.destination === "customer") {
                    setDestination("customer");
                    setBankDetailsId(block.bank_details_id ?? null);
                    setNewBank(block.new_bank_details ?? null);
                  } else if (block.destination === "wallet") {
                    setDestination("wallet");
                    setBankDetailsId(null);
                    setNewBank(null);
                  }
                  // dont_know is hidden on this mount; no action needed.
                }}
              />
            </div>
          )}

          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
              Reason (applies to every selected row)
            </Typography.Text>
            <Select
              placeholder="Select a reason"
              options={REFUND_REASONS}
              value={reason}
              onChange={setReason}
              style={{ width: 320 }}
            />
          </div>

          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
              Notes (optional)
            </Typography.Text>
            <Input.TextArea
              placeholder="A short note for the record"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>

          <Divider style={{ margin: "4px 0" }}>Goods lines</Divider>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            columns={lineColumns}
            dataSource={lines}
            locale={{ emptyText: "No refundable goods lines on this order" }}
          />

          <Divider style={{ margin: "4px 0" }}>Additional charges</Divider>
          {chargesLoading ? (
            <div style={{ textAlign: "center", padding: "8px 0" }}>
              <Spin size="small" />
            </div>
          ) : (
            <>
              {chargesFetchError && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message="Couldn't load refundable charges"
                  description={chargesFetchError}
                />
              )}
              <Table
                rowKey="salesorder_item_id"
                size="small"
                pagination={false}
                columns={chargeColumns}
                dataSource={charges}
                locale={{ emptyText: chargesFetchError
                  ? "Zoho was unreachable — not shown here"
                  : "No refundable charges — either none on the order or all already refunded" }}
              />
            </>
          )}

          {invalidLine && (
            <Alert
              type="error"
              showIcon
              message="One of the selected rows has a qty outside its refundable range — fix it to continue."
            />
          )}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {nothingPicked
                ? "Tick the rows you want to refund"
                : `${pickedLines.length} line(s), ${pickedCharges.length} charge(s) selected`}
            </Typography.Text>
            <Typography.Text strong>
              Total refund: {inr.format(totalRupees)}
            </Typography.Text>
          </div>
        </Space>
      </ModalBody>
    </Modal>

    </>
  );
};

function destinationBlockFromState(
  destination: RefundDestination,
  bankDetailsId: number | null,
): DestinationBlock {
  if (destination === "customer" && bankDetailsId != null) {
    return { destination: "customer", bank_details_id: bankDetailsId };
  }
  return { destination };
}

export default BatchRefundModal;
